import type { ReactNode } from "react";
import { tokens, hairline } from "../tokens";
import { Label } from "./Label";

interface AppShellProps {
	// Brand name set in the header as micro caps.
	title: string;
	// Right side chrome: the signed in role, a sign out control. Omitted on pre auth screens.
	actions?: ReactNode;
	children: ReactNode;
	// Content cap: the dashboard uses the content width, a single column form uses the narrow cap.
	width?: "content" | "form";
}

// The one wrapper every screen passes through. It owns the chrome and the content cap so a screen
// never threads the header through its own signature. The ground is painted at the document level;
// this lays out the header hairline and the centred, gutter kept content column.
export function AppShell({ title, actions, children, width = "content" }: AppShellProps) {
	const cap = width === "form" ? tokens.layout.formMax : tokens.layout.contentMax;
	return (
		<div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
			<header
				style={{
					position: "sticky",
					top: 0,
					zIndex: tokens.z.header,
					background: tokens.color.groundSunken,
					borderBottom: hairline(tokens.color.stroke.hairline),
				}}
			>
				<div
					style={{
						maxWidth: tokens.layout.contentMax,
						margin: "0 auto",
						paddingInline: tokens.layout.gutter,
						minHeight: tokens.touch.min + tokens.space.md,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: tokens.space.lg,
					}}
				>
					<span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space.sm }}>
						<span
							aria-hidden="true"
							style={{
								width: tokens.glyph.dot,
								height: tokens.glyph.dot,
								borderRadius: tokens.radius.pill,
								background: tokens.color.accent.red,
							}}
						/>
						<Label tone="primary">{title}</Label>
					</span>
					{actions && (
						<span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space.md }}>
							{actions}
						</span>
					)}
				</div>
			</header>
			<main
				style={{
					flex: 1,
					width: "100%",
					maxWidth: cap,
					margin: "0 auto",
					paddingInline: tokens.layout.gutter,
					paddingBlock: tokens.space.xxxl,
				}}
			>
				{children}
			</main>
		</div>
	);
}
